import {Trans} from '@lingui/macro';
import React from 'react';

import microsoftDynamicsLogoTiny from '../../../../../../../../../src/assets/images/integrations/microsoft16.svg';
import microsoftDynamicsLogoSm from '../../../../../../../../../src/assets/images/integrations/microsoft20.svg';
import microsoftDynamicsLogo from '../../../../../../../../../src/assets/images/integrations/microsoft24.svg';
import {CrmToFrontCustomFieldMapping} from '../../../../../../../../../src/components/accounts/crm/common/crmSyncHelpers';
import {CrmFieldModel} from '../../../../../../../../../src/models/api/crmFieldModel';
import {
  CrmSyncDefaultFieldMappingIdsEnum,
  CrmSyncIntegrationResourceTypesEnum,
} from '../../../../../../../../../src/models/api/crmSyncModel';
import {IconName} from '../../../../../../../../shared/ui/src/components/icon/icon';
import {CrmSyncOptionsEnum} from '../crmSyncsOptions/crmSyncsOptions';
import {CrmSyncsPageConfig} from './crmSyncsPageConfig';
import {
  computeFrontAccountDefaultFieldIcon,
  CrmSyncAccountDefaultFieldIdsEnum,
  renderAccountDefaultFieldName,
} from './crmSyncsPageConfigHelpers';

/*
 * Constants.
 */

const maxNumberToSync = 100000;

enum MicrosoftDynamicsDefaultAccountFieldsEnum {
  NAME = 'name',
  DOMAINS = 'websiteurl',
}

const microsoftDynamicsRequiredAccountMapping: ReadonlyArray<CrmToFrontCustomFieldMapping> = [
  {
    crmFieldId: MicrosoftDynamicsDefaultAccountFieldsEnum.NAME,
    customFieldId: CrmSyncDefaultFieldMappingIdsEnum.NAME,
  },
];

const microsoftDynamicsOptionalAccountMapping: ReadonlyArray<CrmToFrontCustomFieldMapping> = [
  {
    crmFieldId: MicrosoftDynamicsDefaultAccountFieldsEnum.DOMAINS,
    customFieldId: CrmSyncDefaultFieldMappingIdsEnum.DOMAINS,
  },
];

/*
 * Config.
 */

export const microsoftDynamicsConfig: CrmSyncsPageConfig = {
  logo: microsoftDynamicsLogo,
  logoSm: microsoftDynamicsLogoSm,
  logoTiny: microsoftDynamicsLogoTiny,
  helpCenterLink: 'https://help.front.com/t/m1h7lp2',
  displayName: 'Dynamics 365',
  [CrmSyncIntegrationResourceTypesEnum.ACCOUNT]: {
    maxNumberToSync,
    buildExternalUrl: (baseUrl: string, id: string) =>
      `${baseUrl}/main.aspx?pagetype=entityrecord&etn=account&id=${id}`,
    syncOptionsTitle: (
      <Trans id="crm-syncs-page-microsoft-dynamics-accounts-options-title">
        Choose which accounts to sync
      </Trans>
    ),
    syncOptions: {
      [CrmSyncOptionsEnum.ALL]: {
        optionText: (
          <Trans id="crm-syncs-page-mcirosoft-dynamics-accounts-sync-all-accounts-option">All accounts</Trans>
        ),
        maxCountError: (
          <Trans id="crm-syncs-page-microsoft-dynamics-all-accounts-max-count-error">
            Account limit (100k) exceeded. Please select a Dynamics 365 view.
          </Trans>
        ),
      },
      [CrmSyncOptionsEnum.LISTS]: {
        optionText: (
          <Trans id="crm-syncs-page-microsoft-dynamics-accounts-sync-lists-option">
            From a Dynamics 365 account view
          </Trans>
        ),
        tooltip: (
          <Trans id="crm-syncs-page-microsoft-dynamics-lists-option-tooltip">
            You can create and customize views in Dynamics 365.
          </Trans>
        ),

        dropdownPlaceholder: (
          <Trans id="crm-syncs-page-microsoft-dynamics-lists-option-dropdown-placeholder">Select view</Trans>
        ),
        dropdownTitle: (
          <Trans id="crm-syncs-page-microsoft-dynamics-lists-option-dropdown-title">Dynamics 365 views</Trans>
        ),
        learnMoreLink: 'https://docs.microsoft.com/en-us/dynamics365/sales/customize-views?tabs=SP',
      },
    },
    syncMapping: {
      description: (
        <Trans id="crm-syncs-page-microsoft-dynamics-accounts-mapping-description">
          Select which Dynamics 365 fields to sync to Front and map them to a Front account custom field.
        </Trans>
      ),
      defaultCrmFieldType: 'string',
      computeRequiredMapping: () => microsoftDynamicsRequiredAccountMapping,
      computeOptionalMapping: () => microsoftDynamicsOptionalAccountMapping,
      computeCrmDefaultFieldIcon: (crmField?: CrmFieldModel) =>
        computeCrmAccountDefaultFieldPlaceholderIcon(crmField?.id),
      computeFrontDefaultFieldIcon: computeFrontAccountDefaultFieldIcon,
      computeFrontDefaultFieldName: renderAccountDefaultFieldName,
      computeCrmDefaultFieldPlaceholderName: computeCrmAccountDefaultFieldPlaceholderName,
      computeCrmDefaultFieldPlaceholderIcon: (crmFieldId: string) =>
        computeCrmAccountDefaultFieldPlaceholderIcon(crmFieldId, 'mentionSmall'),
      renderSpecialFieldMessage: (frontFieldId?: number) => {
        if (frontFieldId === CrmSyncAccountDefaultFieldIdsEnum.DOMAINS) {
          return (
            <Trans id="crm-syncs-page-microsoft-dynamics-accounts-mapping-domain-message">
              Contacts with matching email domains will be auto-associated with their account
            </Trans>
          );
        }
        return undefined;
      },
      filterCrmFields: (crmFields) => crmFields,
    },
  },
};

/*
 * Helpers.
 */

function computeCrmAccountDefaultFieldPlaceholderName(customFieldId: string) {
  switch (customFieldId) {
    case MicrosoftDynamicsDefaultAccountFieldsEnum.NAME:
      return 'Account Name';
    case MicrosoftDynamicsDefaultAccountFieldsEnum.DOMAINS:
      return 'Website';
    default:
      return undefined;
  }
}

function computeCrmAccountDefaultFieldPlaceholderIcon(crmFieldId?: string, defaultIconName?: IconName) {
  switch (crmFieldId) {
    case MicrosoftDynamicsDefaultAccountFieldsEnum.DOMAINS:
      return 'mentionSmall';
    case MicrosoftDynamicsDefaultAccountFieldsEnum.NAME:
      return 'contentSmall';
    default:
      return defaultIconName;
  }
}
